import { useEffect, useRef } from "react";
import { useCookieConsent, type CookieConsent } from "@/contexts/CookieConsentContext";

// Google Analytics loads immediately in Consent Mode v2 with all storage
// denied by default. This lets GA collect anonymous, cookieless pings
// (RODO-compliant) so we still get traffic stats before the user decides.
// When the user grants analytics/marketing, we call gtag('consent','update')
// to unlock full measurement + cookies. Leadsy (marketing pixel) is still
// gated behind explicit marketing consent.
export function ConsentScripts() {
  const { consent, hasDecided } = useCookieConsent();
  const loaded = useRef<Record<string, boolean>>({});

  // Load GA + Consent Mode v2 defaults on mount (once).
  useEffect(() => {
    loadGoogleAnalytics(loaded.current);
  }, []);

  // Reflect stored decision on first render.
  useEffect(() => {
    if (!hasDecided) return;
    updateConsent(consent);
    if (consent.marketing) loadLeadsy(loaded.current);
  }, [hasDecided, consent]);

  // React to live changes from the banner.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<CookieConsent>).detail;
      if (!detail) return;
      updateConsent(detail);
      if (detail.marketing) loadLeadsy(loaded.current);
    };
    window.addEventListener("cookieconsentchange", handler);
    return () => window.removeEventListener("cookieconsentchange", handler);
  }, []);

  return null;
}

type GtagWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...a: unknown[]) => void;
};

function ensureGtag(): (...a: unknown[]) => void {
  const w = window as GtagWindow;
  w.dataLayer = w.dataLayer || [];
  if (!w.gtag) {
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments);
    };
  }
  return w.gtag;
}

function loadGoogleAnalytics(state: Record<string, boolean>) {
  if (state['ga']) return;
  state['ga'] = true;
  const id = "G-E4NTT0P0WM";
  const gtag = ensureGtag();

  // Consent Mode v2 — set defaults BEFORE the gtag.js script loads.
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });

  gtag("js", new Date());
  gtag("config", id, {
    anonymize_ip: true,
    // Cookieless pings while analytics_storage is denied.
    // GA will still register anonymous traffic in Consent Mode.
  });

  // Defer the heavy gtag.js download until the main thread is idle.
  // Commands above are already queued on dataLayer, so nothing is lost.
  const inject = () => {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    document.head.appendChild(s);
  };
  // Wait for the first real interaction (or 8s of nothing) so the 166 KB
  // gtag.js parse never competes with the initial render on a phone.
  let done = false;
  const events = ["pointerdown", "keydown", "scroll", "touchstart"] as const;
  const run = () => {
    if (done) return;
    done = true;
    events.forEach((e) => window.removeEventListener(e, run));
    inject();
  };
  events.forEach((e) => window.addEventListener(e, run, { once: true, passive: true }));
  setTimeout(run, 8000);
}

function updateConsent(consent: CookieConsent) {
  const gtag = ensureGtag();
  gtag("consent", "update", {
    analytics_storage: consent.analytics ? "granted" : "denied",
    ad_storage: consent.marketing ? "granted" : "denied",
    ad_user_data: consent.marketing ? "granted" : "denied",
    ad_personalization: consent.marketing ? "granted" : "denied",
  });
}

function loadLeadsy(state: Record<string, boolean>) {
  if (state['leadsy']) return;
  state['leadsy'] = true;
  const s = document.createElement("script");
  s.id = "vtag-ai-js";
  s.async = true;
  s.src = "https://r2.leadsy.ai/tag.js";
  s.setAttribute("data-pid", "pYpX5PIZAlLAebht");
  s.setAttribute("data-version", "062024");
  document.head.appendChild(s);
}
